import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Download, Video, AlertTriangle, Shield, Lightbulb, Wrench, ArrowLeft, Star } from 'lucide-react';

const GuidePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
                style={{ background: 'transparent' }}
              />
            </button>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Home
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Products
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Bulk
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Guides
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Become a Partner
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Login
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" style={{ color: '#8B6F47' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
            </div>
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => {
                const menu = document.getElementById('mobile-menu');
                menu.classList.toggle('hidden');
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <div id="mobile-menu" className="hidden md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Home
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Products
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Bulk
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-lg" style={{ color: '#8B6F47' }}>
              Guides
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Become a Partner
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-lg" style={{ color: '#8B6F47' }}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #D4A574 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <div className="inline-block mb-6 px-6 py-2 rounded-full" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
            <span className="text-sm font-bold">FOR PROFESSIONALS</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold mb-6">
            Installation & Optimization Guide
          </h1>
          <p className="text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-8">
            Expert guidance for installing and maximizing your ONYX
          </p>
          <a 
            href="https://customer-assets.emergentagent.com/job_home-solutions-8/artifacts/r9e531be_OHS%20INSTALLATION%20GUIDE.pdf"
            download="OHS_Installation_Guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button 
              size="lg"
              className="text-white px-8 py-4 rounded-full text-lg font-medium"
              style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
              data-testid="download-guide-button"
            >
              <Download className="mr-2" /> Download Complete Guide PDF
            </Button>
          </a>
        </div>
      </section>

      <div className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">

          {/* Section 1: Safety Basics */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-red-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fef3e4 0%, #fde9cd 100%)' }}>
                <AlertTriangle className="w-6 h-6" style={{ color: '#8B6F47' }} />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#1a2332' }}>Section 1: Safety Basics</h2>
            </div>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="text-lg">
                <strong>Always turn off power at the breaker</strong> before beginning installation. Use a voltage tester to confirm power is off.
              </p>
              <p className="text-lg">
                Wear proper safety equipment including <strong>safety glasses</strong> and <strong>insulated gloves</strong>.
              </p>
              <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-400 rounded">
                <p className="font-bold text-red-800">⚠️ CRITICAL: Never work on live circuits</p>
              </div>
            </div>
          </div>

          {/* Section 2: Why Certifications Matter */}
          <div className="mb-12 p-8 bg-white rounded-2xl border border-gray-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fef3e4 0%, #fde9cd 100%)' }}>
                <Shield className="w-6 h-6" style={{ color: '#8B6F47' }} />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#1a2332' }}>Section 2: Why Certifications Matter</h2>
            </div>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="text-lg">
                <strong>ETL, UL, and CSA certifications</strong> ensure products meet rigorous safety standards.
              </p>
              <p className="text-lg">
                These certifications test for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-lg">
                <li>Electrical safety</li>
                <li>Fire hazards</li>
                <li>Proper insulation</li>
              </ul>
              <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                <p className="font-bold text-blue-800">✓ Never compromise on certified products</p>
              </div>
            </div>
          </div>

          {/* Section 3: Choosing Color Temperatures */}
          <div className="mb-12 p-8 bg-white rounded-2xl border border-gray-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fef3e4 0%, #fde9cd 100%)' }}>
                <Lightbulb className="w-6 h-6" style={{ color: '#8B6F47' }} />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#1a2332' }}>Section 3: Choosing Color Temperatures (2700K–5000K)</h2>
            </div>
            <div className="space-y-6">
              {[
                { temp: '2700K', desc: 'Provides warm, cozy lighting ideal for bedrooms and living rooms', color: '#FFB366' },
                { temp: '3000K-3500K', desc: 'Offers neutral white light perfect for kitchens and bathrooms', color: '#FFF5E6' },
                { temp: '4000K-5000K', desc: 'Delivers cool daylight suitable for workspaces and garages', color: '#CCE6FF' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200">
                  <div 
                    className="w-16 h-16 rounded-lg border-2 border-gray-300 flex-shrink-0"
                    style={{ background: item.color }}
                  ></div>
                  <div>
                    <p className="font-bold text-lg mb-1" style={{ color: '#1a2332' }}>{item.temp}</p>
                    <p className="text-gray-700">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: Maximizing Your ONYX Experience */}
          <div className="mb-12 p-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                <Star className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#1a2332' }}>Section 4: Maximizing Your ONYX Experience</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6">
              Get the most out of your premium ONYX Potlights with these expert optimization tips.
            </p>
            <div className="space-y-6">
              <div className="bg-white border-l-4 border-amber-500 pl-6 py-4 rounded-r-xl shadow-sm">
                <p className="font-bold text-lg mb-2" style={{ color: '#8B6F47' }}>✨ Perfect Color Temperature Selection</p>
                <p className="text-gray-700">Choose 2700K for warm, cozy spaces like bedrooms. Select 4000K for kitchens and workspaces. Use 5000K for task lighting and detailed work areas. The 5-CCT feature lets you customize your ambiance perfectly.</p>
              </div>
              <div className="bg-white border-l-4 border-amber-500 pl-6 py-4 rounded-r-xl shadow-sm">
                <p className="font-bold text-lg mb-2" style={{ color: '#8B6F47' }}>💡 Optimal Dimming Performance</p>
                <p className="text-gray-700">Pair with high-quality LED-compatible dimmers for smooth, flicker-free dimming from 10% to 100%. This extends bulb life and creates the perfect atmosphere for any occasion while saving energy.</p>
              </div>
              <div className="bg-white border-l-4 border-amber-500 pl-6 py-4 rounded-r-xl shadow-sm">
                <p className="font-bold text-lg mb-2" style={{ color: '#8B6F47' }}>🏡 Strategic Placement Tips</p>
                <p className="text-gray-700">Space potlights 4-6 feet apart for even, professional-looking illumination. Place them 2-3 feet from walls to minimize shadows. This creates a luxurious, well-lit environment that showcases your space.</p>
              </div>
              <div className="bg-white border-l-4 border-amber-500 pl-6 py-4 rounded-r-xl shadow-sm">
                <p className="font-bold text-lg mb-2" style={{ color: '#8B6F47' }}>⚡ Energy Savings Maximization</p>
                <p className="text-gray-700">At just 10W per fixture, you're already saving 80% compared to traditional lighting. Use dimmers during evening hours to save even more while extending your 5-year warranty coverage with optimal operating conditions.</p>
              </div>
              <div className="bg-white border-l-4 border-amber-500 pl-6 py-4 rounded-r-xl shadow-sm">
                <p className="font-bold text-lg mb-2" style={{ color: '#8B6F47' }}>🛡️ Maintaining Peak Performance</p>
                <p className="text-gray-700">Keep your ONYX Potlights performing like new: Simply wipe the trim with a soft, dry cloth quarterly. Our sealed design protects against dust and moisture, ensuring years of maintenance-free brilliance.</p>
              </div>
            </div>
          </div>

          {/* Additional Resources Section */}
          <div className="p-12 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2d3748 100%)' }}>
            <h2 className="text-3xl font-bold text-white mb-4">Additional Resources</h2>
            <p className="text-xl text-gray-300 mb-8">
              Access our complete installation guide with detailed diagrams, wiring schematics, and expert optimization techniques.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg"
                className="text-lg px-8 py-6 rounded-full"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                data-testid="download-installation-guide-button"
              >
                <Download className="mr-2" /> Installation Guide
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full border-2 border-white text-white hover:bg-white hover:text-gray-900"
                data-testid="watch-video-tutorial-button"
              >
                <Video className="mr-2" /> Watch Video Tutorial
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-gray-400 text-sm">Premium LED lighting and HVAC solutions for modern homes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX Lighting</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX HVAC</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-gray-400 hover:text-white">About Us</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-gray-400 hover:text-white">Become a Partner</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-gray-400 hover:text-white">Guides</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@onyx-homesolutions.com" className="text-gray-400 hover:text-white">info@onyx-homesolutions.com</a></li>
                <li><a href="tel:519-996-2866" className="text-gray-400 hover:text-white">519-996-2866</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2025 ONYX Home Solutions. All rights reserved. | Powered by Syntrexe</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GuidePage;
